/**
 * Shared listbox helpers for select, autocomplete, pillbox and command.
 */

/** Case- and accent-insensitive text for searching ("Ä" matches "a"). */
export const normalize = (text = '') => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();

const searchText = (option) => normalize(option.dataset.search ?? option.textContent);

/**
 * Hide options that don't contain the query (and groups left without options).
 * Returns the number of visible options.
 */
export function filterOptions(root, query) {
    const needle = normalize(query);
    let visible = 0;

    root.querySelectorAll('[role="option"]').forEach((option) => {
        const match = !needle || searchText(option).includes(needle);
        option.hidden = !match;
        if (match) visible++;
    });

    root.querySelectorAll('[role="group"]').forEach((group) => {
        group.hidden = !group.querySelector('[role="option"]:not([hidden])');
    });

    return visible;
}

/** First option whose text starts with the query (for typeahead). */
export function findByPrefix(options, query) {
    const needle = normalize(query);
    return options.find((option) => searchText(option).startsWith(needle));
}

/**
 * Tracks the highlighted option via aria-activedescendant while DOM focus stays on
 * an input or the listbox itself (WAI-ARIA combobox pattern).
 */
export class ActiveOption {
    constructor(root, focusTarget) {
        this.root = root;
        this.focusTarget = focusTarget;
        this.current = null;
    }

    get options() {
        return [...this.root.querySelectorAll('[role="option"]')].filter(
            (option) => !option.hidden && option.getAttribute('aria-disabled') !== 'true',
        );
    }

    set(option, scroll = true) {
        this.root.querySelectorAll('[role="option"][data-active]').forEach((o) => o.removeAttribute('data-active'));
        this.current = option ?? null;

        const target = typeof this.focusTarget === 'function' ? this.focusTarget() : this.focusTarget;
        if (!option) {
            target?.removeAttribute('aria-activedescendant');
            return;
        }

        option.setAttribute('data-active', '');
        target?.setAttribute('aria-activedescendant', option.id);
        if (scroll) option.scrollIntoView({ block: 'nearest' });
    }

    move(offset) {
        const options = this.options;
        if (!options.length) return;

        const index = options.indexOf(this.current);
        const next = index === -1
            ? (offset > 0 ? 0 : options.length - 1)
            : Math.min(Math.max(index + offset, 0), options.length - 1);

        this.set(options[next]);
    }

    first() {
        this.set(this.options[0]);
    }

    last() {
        this.set(this.options.at(-1));
    }

    /** Keep the highlight valid after filtering or a Livewire morph. */
    refresh() {
        if (!this.current || !this.root.contains(this.current) || this.current.hidden) this.set(null);
    }
}

/**
 * Close a manually controlled popover when the user interacts outside of `host`.
 * Returns a cleanup function.
 */
export function onOutsidePointer(host, callback) {
    const handler = (event) => {
        if (!host.contains(event.target)) callback(event);
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
}
